// src/components/CarBooking/Components/Catalogos/EmpleadosTurnos/EmpleadosTurnosCatalog.jsx
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../../General/Utils/Endpoints';
import { Models } from '../../../../General/Utils/ModelsGeneral';
import EmpleadosTurnosTab from '../../../../RH/Components/Tabs/EmpleadosTurnosTab'; // D&D Component
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import { ENDPOINTS as ENDPOINTSSeguridad } from '../../../../Seguridad/Utils/Endpoints';

const PAGE_SIZE = 10;
const nombreTabla = 'GestiÃ³n de Turnos por Empleado';
const endpoint = ENDPOINTS.empleados;
const modelo = Models.empleado;

export default function EmpleadosTurnosCatalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

  const [page, setPage] = useState(1);
  const [personasMap, setPersonasMap] = useState(new Map());
  const [empresasMap, setEmpresasMap] = useState(new Map()); // idEmpresa -> Nombre
  const [employeeCompanyMap, setEmployeeCompanyMap] = useState(new Map()); // idEmpleado -> idEmpresa

  // 1. Load Employees (filtered by company)
  const loadData = async () => {
    setLoading(true);
    try {
      const idEmpresaSession = getSessionEmpresaId();
      const esSuperAdmin = isSessionSuperAdmin();

      const [resEmpleados, resUOEmpleado, resEmpUO, resEmpresas] = await Promise.all([
        axios.get(ENDPOINTS.empleados),
        axios.get(ENDPOINTSSeguridad.unidadesOrgEmpleado),
        axios.get(ENDPOINTS.empresasUnidadesOrg), // Correct endpoint from General
        axios.get(ENDPOINTS.empresas),
      ]);

      const allEmpleados = (resEmpleados.data || []).map((x) => modelo(x));
      const uoEmpleadoRels = (resUOEmpleado.data || []).map(Models.unidadOrgEmpleado);
      const empUORels = (resEmpUO.data || []).map(Models.empresasUnidadesOrg);
      const empresasList = (resEmpresas.data || []).map(Models.empresas);

      // Maps for easy lookup
      const eMap = new Map();
      empresasList.forEach(e => eMap.set(Number(e.idEmpresa), e.nombre));
      setEmpresasMap(eMap);

      const uoToEmpMap = new Map();
      empUORels.forEach(r => {
        if (r.idUnidadOrganizacional && r.idEmpresa) {
          uoToEmpMap.set(Number(r.idUnidadOrganizacional), Number(r.idEmpresa));
        }
      });

      const empToEmpresaMap = new Map();
      uoEmpleadoRels.forEach(r => {
        const idEmp = uoToEmpMap.get(Number(r.idUnidadOrganizacional));
        if (idEmp) empToEmpresaMap.set(Number(r.idEmpleado), idEmp);
      });
      setEmployeeCompanyMap(empToEmpresaMap);

      let filteredEmpleados = allEmpleados;

      // Filter by company unless SuperAdmin
      if (!esSuperAdmin && idEmpresaSession) {
        const sessionEmpId = Number(idEmpresaSession);
        filteredEmpleados = allEmpleados.filter(e => empToEmpresaMap.get(Number(e.idEmpleado)) === sessionEmpId);
      }

      setRows(filteredEmpleados);
    } catch (e) {
      console.error(e);
      alert('Error al cargar empleados');
    } finally {
      setLoading(false);
    }
  };

  // 2. Load Personas for Name resolution
  const loadPersonas = async () => {
    try {
      const res = await axios.get(ENDPOINTS.personas);
      const map = new Map();
      (res.data || []).forEach(rawP => {
        const p = Models.personas(rawP);
        const fullName = `${p.nombre} ${p.apellidoPaterno} ${p.apellidoMaterno || ''}`.trim();
        map.set(Number(p.idPersona), fullName || '(Sin Nombre)');
      });
      setPersonasMap(map);
    } catch (e) {
      console.error('Error loading personas', e);
    }
  };

  useEffect(() => {
    loadData();
    loadPersonas();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const numero = String(r.numeroEmpleado || '').toLowerCase();
      const nombre = (personasMap.get(r.idPersona) || '').toLowerCase();
      
      const idEmpresaEmp = employeeCompanyMap.get(Number(r.idEmpleado));
      const empresaEmpNombre = (empresasMap.get(idEmpresaEmp) || 'Global').toLowerCase();

      return numero.includes(q) || nombre.includes(q) || empresaEmpNombre.includes(q);
    });
  }, [rows, query, personasMap, empresasMap, employeeCompanyMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleEdit = (id) => {
    setSelectedId(id);
    setModalOpen(true);
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por empleado..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Empleado (Nombre)</th>
                  <th>Número Empleado</th>
                  <th>Empresa</th>
                  <th style={{ textAlign: 'center' }}>Gestionar Turnos</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const nombre = personasMap.get(Number(r.idPersona)) || '(Sin Nombre)';
                  const idEmpresaEmp = employeeCompanyMap.get(Number(r.idEmpleado));
                  
                  return (
                    <tr key={r.idEmpleado}>
                      <td>{normalizeValue(nombre)}</td>
                      <td><strong>{normalizeValue(r.numeroEmpleado)}</strong></td>
                      <td>
                        <span style={{ fontSize: '0.85rem', color: idEmpresaEmp ? '#334155' : '#94a3b8', fontWeight: idEmpresaEmp ? 600 : 400 }}>
                          {idEmpresaEmp ? (empresasMap.get(idEmpresaEmp) || `ID: ${idEmpresaEmp}`) : 'Global'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idEmpleado)}
                          aria-label="Gestionar"
                          title="Gestionar Turnos"
                        >
                          <img src={sigmaEdit} alt="Editar" className="rk-ico" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && paged.length === 0 && (
                  <tr>
                    <td colSpan={4}>Sin resultados.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0 ? (
              <span>
                Mostrando {startIndex}â€“{endIndex} de {filtered.length}
              </span>
            ) : (
              <span>0 resultados</span>
            )}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="â€¹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map((n) => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="â€º"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15,23,42,.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 1000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
          >
            <div
              className="roles-confirm-card"
              style={{
                width: 'min(900px, 96vw)',
                maxHeight: '85vh',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 10px 30px rgba(0,0,0,.25)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: '1rem', // Add padding for the internal component
              }}
            >
              <EmpleadosTurnosTab
                fkValue={selectedId}
                onClose={() => setModalOpen(false)}
                onSaveSuccess={() => {
                  setModalOpen(false);
                  setAlertDialog({ open: true, title: 'Ã‰xito', message: <>Cambios guardados correctamente.<br/>El nuevo horario aplicarÃ¡ a partir del prÃ³ximo lunes.</>, variant: 'success' });
                }}
              />
            </div>
          </div>
        )}
      </div>

      {alertDialog.open && (
        <AlertDialog
          open={alertDialog.open}
          onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
          title={alertDialog.title}
          message={alertDialog.message || ""}
          variant={alertDialog.variant || "info"}
        />
      )}
    </div>
  );
}

